import React, { useEffect, useRef, useState } from 'react';
import {
  SideBarModal,
  Button,
  SearchableDropdown,
  showSuccessToast,
  showErrorToast,
} from '@/components';
import {
  ImportAnchorRole,
  ImportCommitResult,
  ImportOriginMode,
  ImportPreviewResult,
  ImportPreviewShipmentRow,
  ShipmentImportError,
  importShipmentDryRun,
  importShipmentCommit,
  downloadShipmentTemplate,
} from '@/services/api/shipments';
import { getShipmentStatusLabel } from './ShipmentStatusBadge';
import {
  ClientPitstop,
  getListPitstop,
  getMyPitstops,
  Pitstop,
} from '@/services/api/pitstops';
import { useAuth } from '@/contexts/AuthContext';

type Stage = 'pick' | 'preview' | 'committing';

// Origin / fulfilment modes (import TRD §D3). Two are pickable; `DIRECT` still
// renders disabled ("Segera hadir"), so `originMode` never actually holds it.
type PickableOriginMode = Extract<
  ImportOriginMode,
  'DASH_HUB' | 'CLIENT_PITSTOP'
>;

/** Pitstop type of a client-owned pickup point in core masterdata. */
const CLIENT_PITSTOP_TYPE = 'PROVIDER_WAREHOUSE';

/**
 * Height cap for the origin pickers' options panel.
 *
 * The panel is absolutely positioned, so anything taller than the space left
 * below the field inside the modal body counts toward that body's scroll area:
 * the modal turns scrollable the moment the list opens, and the last options
 * end up behind the footer. The default 240px overhangs by ~23px here, so this
 * trades a couple of visible rows for a panel that always fits.
 */
const ORIGIN_MENU_MAX_HEIGHT = 192;

const OriginModeButton: React.FC<{
  active: boolean;
  label: string;
  hint: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}> = ({ active, label, hint, onClick, disabled, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`rounded-md border px-3 py-2 text-left transition-colors ${
      active
        ? 'border-stroke-soft-200 bg-white text-text-strong-950 shadow-sm'
        : onClick && !disabled
          ? 'border-transparent text-text-sub-600 hover:text-text-strong-950'
          : 'cursor-not-allowed border-transparent text-text-soft-400'
    }`}
  >
    <span className="block text-xs font-semibold tracking-tight">{label}</span>
    <span
      className={`mt-0.5 block text-[10px] leading-tight ${
        active ? 'text-text-sub-600' : 'text-text-soft-400'
      }`}
    >
      {hint}
    </span>
  </button>
);

/**
 * Pickup-vs-dropoff role card — richer than OriginModeButton because the
 * direction needs a picture: a small "from → to" chip row plus a sentence.
 */
const AnchorRoleCard: React.FC<{
  active: boolean;
  title: string;
  fromLabel: string;
  toLabel: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  isNew?: boolean;
}> = ({
  active,
  title,
  fromLabel,
  toLabel,
  description,
  onClick,
  disabled,
  isNew,
}) => {
  const chip = `rounded-md border px-1.5 py-0.5 ${
    active
      ? 'border-primary/40 bg-primary/5 text-primary'
      : 'border-stroke-soft-200 text-text-sub-600'
  }`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`rounded-lg border bg-bg-white-0 p-3 text-left transition-colors ${
        active
          ? 'border-primary shadow-sm ring-1 ring-primary'
          : 'border-stroke-soft-200 text-text-sub-600 hover:border-stroke-sub-300'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span className="flex items-center gap-2 text-xs font-semibold tracking-tight text-text-strong-950">
        {title}
        {isNew && (
          <span className="rounded-full bg-warning-lighter px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-warning-dark ring-1 ring-inset ring-warning-light">
            Baru
          </span>
        )}
      </span>
      <span className="mt-1.5 flex items-center gap-1.5 text-[10px] font-semibold">
        <span className={chip}>{fromLabel}</span>
        <span className="text-text-soft-400" aria-hidden="true">
          →
        </span>
        <span className={chip}>{toLabel}</span>
      </span>
      <span className="mt-1.5 block text-[10px] leading-relaxed text-text-sub-600">
        {description}
      </span>
    </button>
  );
};

/** The route an import preview is about to create, for the preview banner. */
interface PreviewRoute {
  originMode: ImportOriginMode;
  anchorRole: ImportAnchorRole;
  /** Name of the hub / pitstop anchor; null when unknown (or DIRECT). */
  anchorName: string | null;
  /** Anchor address from the dry-run echo — lets a row tell "goes to the
   *  anchor" apart from "goes to its own CSV address". */
  anchorAddress: string | null;
}

const ORIGIN_MODE_LABEL: Record<ImportOriginMode, string> = {
  DASH_HUB: 'Hub Dash',
  CLIENT_PITSTOP: 'Pitstop Saya',
  DIRECT: 'Pickup Langsung',
};

/**
 * One-line "where do these goods go" banner on the preview step: mode, role and
 * the from → to direction with the anchor named, mirroring the role card the
 * client picked on the form.
 */
const PreviewRouteBanner: React.FC<{ route: PreviewRoute }> = ({ route }) => {
  const direct = route.originMode === 'DIRECT';
  const dropoff = !direct && route.anchorRole === 'DESTINATION';
  const anchor =
    route.anchorName || (route.originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop');
  const anchorChip =
    'rounded-md border border-primary/40 bg-primary/5 px-1.5 py-0.5 text-primary';
  const csvChip =
    'rounded-md border border-stroke-soft-200 bg-bg-white-0 px-1.5 py-0.5 text-text-sub-600';
  const from = direct ? 'Alamat pickup di CSV' : dropoff ? 'Alamat pickup di CSV' : anchor;
  const to = direct ? 'Alamat penerima di CSV' : dropoff ? anchor : 'Alamat penerima di CSV';
  return (
    <div
      role="note"
      aria-label="Rute import"
      className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs font-semibold text-text-strong-950">
          <i className="ri-route-line text-base text-primary" aria-hidden="true" />
          {ORIGIN_MODE_LABEL[route.originMode]}
          {!direct && (
            <>
              <span className="text-text-soft-400" aria-hidden="true">
                ·
              </span>
              {dropoff ? 'Titik Dropoff' : 'Titik Pickup'}
            </>
          )}
        </p>
        <p className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold">
          <span className={dropoff || direct ? csvChip : anchorChip}>{from}</span>
          <span className="text-text-soft-400" aria-hidden="true">
            →
          </span>
          <span className={dropoff ? anchorChip : csvChip}>{to}</span>
        </p>
      </div>
      {dropoff && (
        <p className="mt-1.5 text-[11px] text-text-sub-600">
          Baris yang alamat dropoff-nya kosong di-dropoff ke{' '}
          <span className="font-semibold text-text-strong-950">{anchor}</span>;
          baris yang mengisi alamat sendiri memakai alamat di CSV.
        </p>
      )}
    </div>
  );
};

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  onImported: (result: ImportCommitResult) => void;
}

const friendlyImportError = (err: unknown): string => {
  if (err instanceof ShipmentImportError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
};

const csvEscape = (value: string): string => {
  if (/[,"\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
};

const buildErrorCsv = (preview: ImportPreviewResult): Blob => {
  const header = ['row_number', 'field', 'value', 'message'];
  const lines = [header.join(',')];
  for (const e of preview.errors) {
    lines.push(
      [
        String(e.rowNumber),
        csvEscape(e.field),
        csvEscape(e.value ?? ''),
        csvEscape(e.message),
      ].join(',')
    );
  }
  return new Blob(['﻿' + lines.join('\r\n') + '\r\n'], {
    type: 'text/csv;charset=utf-8',
  });
};

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onImported,
}) => {
  const { user, client } = useAuth();
  const [stage, setStage] = useState<Stage>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [activeTab, setActiveTab] = useState<
    'toCreate' | 'duplicates' | 'errors'
  >('toCreate');
  const [appendConfirmOpen, setAppendConfirmOpen] = useState(false);

  // Where the goods pass through (the anchor LOCATION) + the Dash Hub picked
  // for DASH_HUB.
  const [originMode, setOriginMode] = useState<PickableOriginMode>('DASH_HUB');
  // Which side that hub/pitstop plays (UI: "dipakai sebagai Titik Pickup /
  // Titik Dropoff"). ORIGIN = legacy (goods depart FROM it); DESTINATION =
  // retrieval (rows are picked up at their CSV origin and dropped off AT it).
  const [anchorRole, setAnchorRole] = useState<ImportAnchorRole>('ORIGIN');
  const [hubId, setHubId] = useState('');
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  // CLIENT_PITSTOP: the client's OWN pickup point (`PROVIDER_WAREHOUSE`) a
  // rider collects from — mirrors the hub picker, scoped to this client.
  const [pitstopId, setPitstopId] = useState('');
  const [pitstops, setPitstops] = useState<ClientPitstop[]>([]);
  const [pitstopsLoading, setPitstopsLoading] = useState(false);
  const [pitstopsError, setPitstopsError] = useState<string | null>(null);
  // Server-side search: the list is paged (size=100), so filtering only what
  // that first page happened to contain would hide matches for any client with
  // more pitstops than that. The debounced term drives a refetch instead.
  const [pitstopSearch, setPitstopSearch] = useState('');
  const pitstopSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // The picked pitstop's name, kept independently of `pitstops` so it stays
  // visible in the closed input after a later search narrows it out of the
  // options.
  const [pitstopLabel, setPitstopLabel] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Highlights the dropzone while a file is being dragged over it.
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStage('pick');
    setFile(null);
    setSubmitting(false);
    setInlineError(null);
    setPreview(null);
    setActiveTab('toCreate');
    setAppendConfirmOpen(false);
    setOriginMode('DASH_HUB');
    setAnchorRole('ORIGIN');
    setDragActive(false);
    setHubId('');
    setPitstopId('');
    setPitstopLabel('');
    setPitstopSearch('');
  }, [isOpen]);

  // Load the Dash Hubs (staffed) once the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setHubsLoading(true);
    getListPitstop({ type: 'HUB', hasStaff: true, page: '1', size: '100' })
      .then((res) => {
        if (active) setHubs(res.data ?? []);
      })
      .catch(() => {
        if (active) setHubs([]);
      })
      .finally(() => {
        if (active) setHubsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  // Load this client's own pitstops once CLIENT_PITSTOP is picked, and again
  // whenever the debounced search term changes. No client id to pass: the
  // tenant scope is derived from the bearer token server-side, so there is
  // nothing to re-filter here either.
  //
  // `active` also settles races: a slow response for an earlier term can't
  // overwrite the list for the term the user has since typed.
  useEffect(() => {
    if (!isOpen || originMode !== 'CLIENT_PITSTOP') return;
    let active = true;
    setPitstopsLoading(true);
    setPitstopsError(null);
    getMyPitstops({
      type: CLIENT_PITSTOP_TYPE,
      page: '1',
      size: '100',
      ...(pitstopSearch ? { search: pitstopSearch } : {}),
    })
      .then((res) => {
        if (active) setPitstops(res.data ?? []);
      })
      .catch((err) => {
        if (!active) return;
        setPitstops([]);
        setPitstopsError(
          err instanceof Error ? err.message : 'Gagal memuat pitstop'
        );
      })
      .finally(() => {
        if (active) setPitstopsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen, originMode, pitstopSearch]);

  // Drop a pending search on unmount so it can't fire into a dead component.
  useEffect(
    () => () => {
      if (pitstopSearchDebounce.current) {
        clearTimeout(pitstopSearchDebounce.current);
      }
    },
    []
  );

  const handlePitstopSearch = (term: string) => {
    if (pitstopSearchDebounce.current) {
      clearTimeout(pitstopSearchDebounce.current);
    }
    pitstopSearchDebounce.current = setTimeout(() => {
      setPitstopSearch(term.trim());
    }, 500);
  };

  const handlePitstopChange = (next: string) => {
    setPitstopId(next);
    setPitstopLabel(
      pitstops.find((ps) => String(ps.id) === next)?.name ?? ''
    );
  };

  // Each mode keeps its own location pick, so toggling to look at the other
  // mode and back doesn't lose it. Only the active mode's id is ever sent —
  // see `originIds` — which is what keeps the BE from answering
  // IMPORT_ORIGIN_CONFLICT (it rejects hubId and pitstopId arriving together).
  const handleOriginModeChange = (next: PickableOriginMode) => {
    if (next === originMode) return;
    setOriginMode(next);
    setInlineError(null);
  };

  // Where the goods start, as the API wants it: exactly one of the two ids.
  const originIds =
    originMode === 'DASH_HUB'
      ? { hubId: hubId ? Number(hubId) : undefined, pitstopId: undefined }
      : {
          hubId: undefined,
          pitstopId: pitstopId ? Number(pitstopId) : undefined,
        };

  // Mode-aware gate for the upload box + Preview button.
  const hasOriginLocation =
    originMode === 'DASH_HUB' ? Boolean(hubId) : Boolean(pitstopId);

  const handleFile = (f: File | null) => {
    if (f && f.size > 5 * 1024 * 1024) {
      setInlineError('File terlalu besar. Maks 5 MB.');
      return;
    }
    setFile(f);
    setInlineError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setInlineError(null);
    setSubmitting(true);
    try {
      const result = await importShipmentDryRun({
        clientId,
        file,
        originMode,
        anchorRole,
        ...originIds,
      });
      setPreview(result);
      setStage('preview');
      const next: 'toCreate' | 'duplicates' | 'errors' =
        result.summary.errors > 0
          ? 'errors'
          : result.summary.duplicates > 0
            ? 'duplicates'
            : 'toCreate';
      setActiveTab(next);
    } catch (err) {
      const msg = friendlyImportError(err);
      if (
        err instanceof ShipmentImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(msg);
      } else {
        setInlineError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.errors > 0) return;
    if (preview.summary.duplicates > 0) {
      setAppendConfirmOpen(true);
      return;
    }
    void doCommit();
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    setAppendConfirmOpen(false);
    setStage('committing');
    setSubmitting(true);
    setInlineError(null);
    try {
      const result = await importShipmentCommit({
        clientId,
        file,
        originMode,
        anchorRole,
        ...originIds,
        // Provider-user tokens have no email; attribute the upload to the
        // logged-in user so the BE doesn't fall back to a blank uploadedBy.
        uploadedBy:
          user?.email || user?.displayName || user?.username || undefined,
      });
      onImported(result);
      const parts: string[] = [];
      if (result.appendedItems > 0)
        parts.push(`${result.appendedItems} item ditambahkan`);
      if (result.skippedDuplicateItems > 0)
        parts.push(`${result.skippedDuplicateItems} dilewati`);
      showSuccessToast(
        `Berhasil mengimpor ${result.createdShipments} shipment`,
        parts.join(' · ') || 'Import selesai.'
      );
      onClose();
    } catch (err) {
      if (
        err instanceof ShipmentImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(
          err.message ||
            'File tidak cocok dengan kondisi database saat ini. Coba preview ulang.'
        );
      } else {
        setStage('preview');
        const msg = friendlyImportError(err);
        setInlineError(msg);
        showErrorToast('Import gagal', msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Resolve the flag fresh at click time (not via a reactive hook) so a cold
  // Remote Config cache can't make us fall back to the general template before
  // the flag has loaded — the bug where a dedicated client got the general file
  // until a page refresh.
  // The BE picks the variant (general / client-pitstop / dedicated) from the
  // origin mode and the client, so there is nothing to resolve here — the
  // per-client dedicated list moved from a Remote Config flag to the
  // DEDICATED_IMPORT_SHIPMENTS_TEMPLATE_CLIENT_IDS env in logistic-service.
  const handleDownloadTemplate = async () => {
    setInlineError(null);
    try {
      await downloadShipmentTemplate({
        originMode,
        // Titik Dropoff gets its own sheet (pickup columns required,
        // destination columns optional).
        anchorRole,
        clientId: client?.id ?? clientId,
      });
    } catch (err) {
      const msg = friendlyImportError(err);
      setInlineError(msg);
      showErrorToast('Gagal mengunduh template', msg);
    }
  };

  const handleDownloadErrors = () => {
    if (!preview) return;
    const blob = buildErrorCsv(preview);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shipment-import-errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!hasOriginLocation) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  // dragleave also fires when the cursor crosses into a child; the very next
  // dragover on the container flips it back on, so no flicker guard needed.
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasOriginLocation) setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // The anchor noun as the rest of the copy uses it.
  const anchorNoun = originMode === 'DASH_HUB' ? 'hub' : 'pitstop';

  const affectedShipments = preview
    ? preview.summary.toCreateShipments + preview.summary.toAppendShipments
    : 0;

  // What the preview is about to import, stated back to the client so a wrong
  // Titik Pickup / Titik Dropoff pick is caught BEFORE Konfirmasi. The dry-run
  // echo is authoritative; the form state is the fallback for older BE builds.
  const previewRoute: PreviewRoute | null = preview
    ? {
        originMode: preview.summary.originMode ?? originMode,
        anchorRole: preview.summary.anchorRole ?? anchorRole,
        anchorName:
          preview.summary.origin?.name ??
          (originMode === 'DASH_HUB'
            ? hubs.find((h) => String(h.id) === hubId)?.name
            : pitstopLabel) ??
          null,
        anchorAddress: preview.summary.origin?.address ?? null,
      }
    : null;

  const blockCommit =
    !preview || preview.summary.errors > 0 || affectedShipments === 0;

  const modalHeader = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
        <i className="ri-upload-2-line text-lg text-primary" />
      </div>
      <div>
        <h2 className="text-base font-semibold tracking-tight text-text-strong-950">
          Import Shipment dari CSV
        </h2>
        <p className="text-xs text-text-sub-600">
          Upload file CSV. Kami akan mengelompokkan baris berdasarkan AWB.
        </p>
      </div>
    </div>
  );

  const pickFooter = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="white" onClick={onClose} disabled={submitting}>
        Batal
      </Button>
      <Button
        onClick={handlePreview}
        disabled={!file || !hasOriginLocation || submitting}
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-sm" />
            Memproses…
          </>
        ) : (
          'Preview import'
        )}
      </Button>
    </div>
  );

  const previewFooter = preview && (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="white"
        onClick={() => {
          setStage('pick');
          setPreview(null);
          setInlineError(null);
        }}
        disabled={submitting}
      >
        Kembali
      </Button>
      <Button variant="white" onClick={onClose} disabled={submitting}>
        Batal
      </Button>
      <Button onClick={handleConfirm} disabled={blockCommit || submitting}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-sm" />
            Mengimpor…
          </>
        ) : (
          `Konfirmasi import${affectedShipments > 0 ? ` (${affectedShipments})` : ''}`
        )}
      </Button>
    </div>
  );

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={onClose}
        header={modalHeader}
        width="xl"
        closeOnBackdropClick={!submitting}
        footer={stage === 'pick' ? pickFooter : (previewFooter ?? undefined)}
      >
        {stage === 'pick' && (
          <div className="space-y-5 p-5">
            {/* Mode = the anchor LOCATION; its pickup/dropoff ROLE is the
                section below. */}
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                Barang lewat mana? <span className="text-error-base">*</span>
              </label>
              <div className="grid grid-cols-1 gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1 sm:grid-cols-3">
                <OriginModeButton
                  active={originMode === 'DASH_HUB'}
                  onClick={() => handleOriginModeChange('DASH_HUB')}
                  disabled={submitting}
                  label="Hub Dash"
                  hint="Barang mampir / berangkat dari hub"
                />
                <OriginModeButton
                  active={originMode === 'CLIENT_PITSTOP'}
                  onClick={() => handleOriginModeChange('CLIENT_PITSTOP')}
                  disabled={submitting}
                  label="Pitstop Saya"
                  hint="Barang mampir / berangkat dari gudang kamu"
                />
                <OriginModeButton
                  active={false}
                  disabled
                  title="Segera hadir — dispatch untuk Pickup Langsung masih disiapkan"
                  label="Pickup Langsung"
                  hint="Segera hadir"
                />
              </div>
            </div>

            {/* Anchor role — the hub/pitstop as pickup point (legacy) or as
                dropoff/collection point (retrieval). */}
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                {originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'} dipakai sebagai{' '}
                <span className="text-error-base">*</span>
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AnchorRoleCard
                  active={anchorRole === 'ORIGIN'}
                  onClick={() => setAnchorRole('ORIGIN')}
                  disabled={submitting}
                  title="Titik Pickup"
                  fromLabel={originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'}
                  toLabel="Alamat dropoff"
                  description={`Barang di-pickup rider dari ${anchorNoun}, lalu di-dropoff ke alamat penerima di file CSV.`}
                />
                <AnchorRoleCard
                  active={anchorRole === 'DESTINATION'}
                  onClick={() => setAnchorRole('DESTINATION')}
                  disabled={submitting}
                  isNew
                  title="Titik Dropoff"
                  fromLabel="Alamat pickup"
                  toLabel={originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'}
                  description={`Rider pickup barang di alamat-alamat pengirim di file CSV, lalu semuanya di-dropoff ke ${anchorNoun}.`}
                />
              </div>
            </div>

            {originMode === 'DASH_HUB' ? (
              /* Dash Hub asal — origin hub the shipments depart from. */
              <div>
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  Dash Hub <span className="text-error-base">*</span>
                </label>
                <SearchableDropdown
                  options={hubs.map((h) => ({ value: h.id, label: h.name }))}
                  value={hubId}
                  menuMaxHeight={ORIGIN_MENU_MAX_HEIGHT}
                  onChange={(v) => setHubId(String(v))}
                  disabled={hubsLoading}
                  loading={hubsLoading}
                  placeholder={
                    hubsLoading ? 'Memuat hub…' : 'Cari atau pilih Dash Hub…'
                  }
                  emptyMessage="Hub tidak ditemukan"
                />
                <p className="mt-2 text-[11px] text-text-soft-400">
                  {anchorRole === 'DESTINATION' ? (
                    'Semua barang di file ini akan dikumpulkan (di-dropoff) ke hub ini.'
                  ) : (
                    <>
                      Shipment berangkat dari <b>Dash Hub</b> (X-dock). Origin
                      diambil dari hub yang dipilih, bukan ditebak dari
                      destination.
                    </>
                  )}
                </p>
              </div>
            ) : (
              /* Pitstop saya — the client's own pickup point. */
              <div>
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  Pitstop saya <span className="text-error-base">*</span>
                </label>
                <SearchableDropdown
                  options={pitstops.map((ps) => ({
                    value: ps.id,
                    label: ps.name,
                  }))}
                  value={pitstopId}
                  menuMaxHeight={ORIGIN_MENU_MAX_HEIGHT}
                  selectedLabelFallback={pitstopLabel}
                  onChange={(v) => handlePitstopChange(String(v))}
                  onSearchChange={handlePitstopSearch}
                  // The server already applied the search; filtering the
                  // response again locally would drop rows that matched on
                  // code or address rather than name.
                  disableLocalFilter
                  loading={pitstopsLoading}
                  // Deliberately not `disabled` while loading: every keystroke
                  // triggers a refetch, and disabling the input mid-search
                  // would blur it and swallow what the user is typing.
                  placeholder={
                    pitstopsLoading && pitstops.length === 0
                      ? 'Memuat pitstop…'
                      : 'Cari atau pilih pitstop…'
                  }
                  emptyMessage={
                    pitstopsError ??
                    (pitstopsLoading
                      ? 'Mencari…'
                      : pitstopSearch
                        ? `Tidak ada pitstop cocok dengan "${pitstopSearch}"`
                        : 'Belum ada pitstop terdaftar untuk akun ini')
                  }
                />
                <p className="mt-2 text-[11px] text-text-soft-400">
                  {anchorRole === 'DESTINATION' ? (
                    'Semua barang di file ini akan dikumpulkan (di-dropoff) ke pitstop kamu ini.'
                  ) : (
                    <>
                      Barang <b>tetap di pitstop kamu</b> dan dijemput rider —
                      tidak perlu scan masuk hub. Origin diambil dari masterdata
                      pitstop ini.
                    </>
                  )}
                </p>
                {pitstopsError && (
                  <p className="mt-1 text-[11px] text-error-base">
                    {pitstopsError}
                  </p>
                )}
              </div>
            )}

            {/* Dispatch process. The scheduled variant (auto-dispatch on the
                pitstop's slots) is set up and run by Dash ops — the schedule
                endpoints are admin-only — so it renders disabled here and the
                upload always takes the unscheduled path. */}
            {/* Pickup role only: a retrieval (Titik Dropoff) has no departure
                slot at the pitstop, so there is no dispatch process to pick. */}
            {originMode === 'CLIENT_PITSTOP' && anchorRole === 'ORIGIN' && (
              <div>
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  Proses dispatch <span className="text-error-base">*</span>
                </label>
                <div className="grid grid-cols-1 gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1 sm:grid-cols-2">
                  <OriginModeButton
                    active
                    label="Tanpa Jadwal"
                    hint="Rider jemput, dispatch diatur ops"
                  />
                  <OriginModeButton
                    active={false}
                    disabled
                    title="Jadwal dispatch diatur oleh ops Dash"
                    label="Pakai Jadwal"
                    hint="Diatur ops Dash"
                  />
                </div>
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
                  <i
                    className="ri-information-line mt-px shrink-0 text-sm text-text-sub-600"
                    aria-hidden="true"
                  />
                  <p className="text-[11px] leading-relaxed text-text-sub-600">
                    <span className="font-semibold text-text-strong-950">
                      Tanpa jadwal dispatch.
                    </span>{' '}
                    Rider jemput di pitstop kamu dan barang tidak perlu scan
                    masuk hub — item langsung siap dispatch begitu import
                    selesai. Ops Dash yang membuat dispatch saat barang siap.
                  </p>
                </div>
              </div>
            )}

            <div>
              {/* Header + Download template: always interactive — the template
                  doesn't depend on the chosen hub, so it's not hub-gated. */}
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  File CSV <span className="text-error-base">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => void handleDownloadTemplate()}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Download template
                </button>
              </div>
              {/* File upload stays gated until an origin location is chosen. */}
              <div
                className={
                  hasOriginLocation ? '' : 'pointer-events-none opacity-50'
                }
                aria-disabled={!hasOriginLocation}
              >
                {/* Whole zone is the click target while empty; once a file is
                    picked it becomes a card with its own Ganti/Hapus actions
                    (dropping a new file still replaces it either way). */}
                <div
                  role="button"
                  tabIndex={file || !hasOriginLocation ? -1 : 0}
                  onClick={() => {
                    if (!file && !submitting && hasOriginLocation)
                      fileInputRef.current?.click();
                  }}
                  onKeyDown={(e) => {
                    if (
                      !file &&
                      !submitting &&
                      hasOriginLocation &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragEnter={onDragOver}
                  onDragLeave={onDragLeave}
                  className={`rounded-xl border-2 border-dashed text-center transition-colors ${
                    dragActive
                      ? 'border-primary bg-primary/5'
                      : file
                        ? 'border-stroke-soft-200 bg-bg-white-0'
                        : 'cursor-pointer border-stroke-sub-300 bg-bg-weak-50 hover:border-primary/60 hover:bg-bg-white-0'
                  } ${file ? 'px-4 py-3.5' : 'px-4 py-8'}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv,application/vnd.ms-excel"
                    className="hidden"
                    aria-label="Pilih file CSV"
                    disabled={!hasOriginLocation}
                    onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                  />
                  {file ? (
                    <div className="flex items-center justify-between gap-3 text-left">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-verified-light bg-verified-lighter">
                          <i
                            className="ri-file-text-line text-xl text-verified-base"
                            aria-hidden="true"
                          />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-text-strong-950">
                            {file.name}
                          </p>
                          <p className="mt-0.5 text-xs text-text-sub-600">
                            <i
                              className="ri-checkbox-circle-fill mr-1 text-verified-base"
                              aria-hidden="true"
                            />
                            {(file.size / 1024).toFixed(1)} KB · siap di-preview
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          disabled={submitting}
                          className="rounded-md border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1.5 text-xs font-semibold text-text-sub-600 transition-colors hover:text-text-strong-950 disabled:opacity-50"
                        >
                          Ganti file
                        </button>
                        <button
                          type="button"
                          aria-label="Hapus file"
                          title="Hapus file"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFile(null);
                          }}
                          disabled={submitting}
                          className="flex size-8 items-center justify-center rounded-md text-text-sub-600 transition-colors hover:bg-error-lighter hover:text-error-base disabled:opacity-50"
                        >
                          <i
                            className="ri-delete-bin-line text-[15px]"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="pointer-events-none flex flex-col items-center gap-2.5">
                      <span
                        className={`flex size-11 items-center justify-center rounded-full border transition-colors ${
                          dragActive
                            ? 'border-primary/40 bg-primary/10'
                            : 'border-stroke-soft-200 bg-bg-white-0 shadow-sm'
                        }`}
                      >
                        <i
                          className={`ri-upload-cloud-2-line text-[22px] ${
                            dragActive ? 'text-primary' : 'text-text-sub-600'
                          }`}
                          aria-hidden="true"
                        />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-text-strong-950">
                          {dragActive ? (
                            'Lepas file di sini'
                          ) : (
                            <>
                              Drag &amp; drop file CSV, atau{' '}
                              <span className="font-semibold text-primary">
                                pilih file
                              </span>
                            </>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-text-soft-400">
                          Format .csv · maks 5 MB
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-text-soft-400">
                  {!hasOriginLocation ? (
                    originMode === 'DASH_HUB' ? (
                      'Pilih Dash Hub terlebih dahulu untuk mengaktifkan upload file.'
                    ) : (
                      'Pilih pitstop terlebih dahulu untuk mengaktifkan upload file.'
                    )
                  ) : anchorRole === 'DESTINATION' ? (
                    <>
                      <span className="font-semibold text-text-strong-950">
                        Kolom wajib: Receiver Name, Item Name, dan alamat pickup
                        di tiap baris
                      </span>{' '}
                      (tempat rider menjemput barang). Kolom alamat dropoff
                      boleh dikosongkan — baris yang kosong otomatis di-dropoff
                      ke {anchorNoun} yang dipilih; baris yang mengisi alamat
                      dropoff sendiri memakai alamat di CSV. Semua barang di
                      file ini jadi satu rombongan pickup. Lihat template untuk
                      set kolom.
                    </>
                  ) : originMode === 'CLIENT_PITSTOP' ? (
                    <>
                      Kolom wajib: Receiver Name &amp; Item Name. Alamat pickup
                      diambil dari pitstop yang dipilih selama kolom Origin di
                      CSV kosong; baris yang mengisi Origin Lat/Long sendiri
                      memakai koordinat CSV-nya (Origin Notes tetap dipakai
                      sebagai catatan pickup untuk rider). Lihat template untuk
                      set kolom.
                    </>
                  ) : (
                    <>
                      Kolom wajib: Receiver Name &amp; Item Name. Lokasi tujuan
                      diisi otomatis dari masterdata (cocokkan Receiver Name),
                      origin dari hub terpilih. Origin/Destination Notes
                      opsional — isinya tampil sebagai catatan di app rider.
                      Lihat template untuk set kolom.
                    </>
                  )}
                </p>
              </div>
            </div>

            {inlineError && (
              <div className="rounded-lg border border-stroke-soft-200 bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}

        {(stage === 'preview' || stage === 'committing') && preview && (
          <div className="space-y-4 p-5">
            {previewRoute && <PreviewRouteBanner route={previewRoute} />}
            <PreviewSummary preview={preview} />

            {preview.summary.ignoredColumns.length > 0 && (
              <div className="rounded-lg border border-stroke-soft-200 bg-warning-lighter px-3 py-2 text-xs text-warning-dark">
                Kolom tidak dikenal diabaikan:{' '}
                <span className="font-mono">
                  {preview.summary.ignoredColumns.join(', ')}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
              <PreviewTab
                active={activeTab === 'toCreate'}
                onClick={() => setActiveTab('toCreate')}
                label="Akan dibuat"
                count={preview.summary.toCreateShipments}
                tone="success"
              />
              <PreviewTab
                active={activeTab === 'duplicates'}
                onClick={() => setActiveTab('duplicates')}
                label="Duplikat"
                count={preview.summary.duplicates}
                tone="warning"
              />
              <PreviewTab
                active={activeTab === 'errors'}
                onClick={() => setActiveTab('errors')}
                label="Error"
                count={preview.summary.errors}
                tone="error"
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-stroke-soft-200">
              {activeTab === 'toCreate' && (
                <ToCreateTable preview={preview} route={previewRoute} />
              )}
              {activeTab === 'duplicates' && (
                <DuplicatesTable preview={preview} />
              )}
              {activeTab === 'errors' && (
                <ErrorsTable
                  preview={preview}
                  onDownload={handleDownloadErrors}
                />
              )}
            </div>

            {inlineError && (
              <div className="rounded-lg border border-stroke-soft-200 bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}
      </SideBarModal>

      {/* Append-confirm modal */}
      {preview && appendConfirmOpen && (
        <SideBarModal
          isOpen={appendConfirmOpen}
          onClose={() => !submitting && setAppendConfirmOpen(false)}
          closeOnBackdropClick={!submitting}
          width="sm"
          header={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-lighter">
                <i className="ri-git-merge-line text-lg text-warning-dark" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight text-text-strong-950">
                  {preview.summary.duplicates} booking ID sudah ada
                </h2>
                <p className="text-xs text-text-sub-600">
                  Item baru akan ditambahkan ke shipment yang ada.
                </p>
              </div>
            </div>
          }
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="white"
                onClick={() => setAppendConfirmOpen(false)}
                disabled={submitting}
              >
                Batal
              </Button>
              <Button onClick={() => void doCommit()} disabled={submitting}>
                Tambahkan &amp; import
              </Button>
            </div>
          }
        >
          <ul className="space-y-2 p-5 text-xs text-text-sub-600">
            <li>
              <strong className="text-text-strong-950">
                {preview.summary.toCreateShipments}
              </strong>{' '}
              shipment baru dibuat,{' '}
              <strong className="text-text-strong-950">
                {preview.summary.toAppendItems}
              </strong>{' '}
              item ditambahkan ke AWB yang sudah ada.
            </li>
            {preview.summary.skippedDuplicateItems > 0 && (
              <li>
                <strong className="text-text-strong-950">
                  {preview.summary.skippedDuplicateItems}
                </strong>{' '}
                baris dilewati — nama item sudah ada pada shipment.
              </li>
            )}
            <li className="text-text-soft-400">
              AWB dengan status <strong>Dispatched</strong> yang mendapatkan item
              baru akan berubah menjadi <strong>Partially Dispatched</strong>;
              AWB yang sudah <strong>Completed</strong> akan mundur ke{' '}
              <strong>At Hub</strong>. AWB yang dibatalkan tidak akan diaktifkan
              kembali.
            </li>
          </ul>
        </SideBarModal>
      )}
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const PreviewSummary: React.FC<{ preview: ImportPreviewResult }> = ({
  preview,
}) => {
  const cards = [
    {
      label: 'Shipment baru',
      value: preview.summary.toCreateShipments,
      cls: 'text-success-dark bg-success-lighter',
    },
    {
      label: 'Item baru',
      value: preview.summary.toCreateItems,
      cls: 'text-primary bg-primary/10',
    },
    {
      label: 'Item ditambah',
      value: preview.summary.toAppendItems,
      cls: 'text-warning-dark bg-warning-lighter',
    },
    {
      label: 'Error',
      value: preview.summary.errors,
      cls: 'text-error-base bg-error-lighter',
    },
  ];
  return (
    <div>
      <p className="mb-2 text-xs text-text-soft-400">
        {preview.summary.totalRows} baris diproses
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className={`rounded-lg ${card.cls} px-4 py-3`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]">
              {card.label}
            </p>
            <p className="mt-2 text-2xl font-bold tracking-tight tabular-nums">
              {card.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PreviewTabProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone: 'success' | 'warning' | 'error';
}

const PreviewTab: React.FC<PreviewTabProps> = ({
  active,
  onClick,
  label,
  count,
  tone,
}) => {
  const toneActive =
    tone === 'success'
      ? 'bg-success-lighter text-success-dark'
      : tone === 'warning'
        ? 'bg-warning-lighter text-warning-dark'
        : 'bg-error-lighter text-error-base';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold tracking-tight transition-colors ${
        active
          ? 'border border-stroke-soft-200 bg-bg-white-0 text-text-strong-950'
          : 'border border-transparent text-text-sub-600 hover:text-text-strong-950'
      }`}
    >
      {label}
      <span
        className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
          active ? toneActive : 'bg-bg-soft-200 text-text-soft-400'
        }`}
      >
        {count}
      </span>
    </button>
  );
};

const ToCreateTable: React.FC<{
  preview: ImportPreviewResult;
  route: PreviewRoute | null;
}> = ({ preview, route }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (preview.summary.toCreateShipments === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-soft-400">
        Tidak ada shipment baru yang akan dibuat.
      </div>
    );
  }

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    // `contain: inline-size` keeps the table's min-content width (8 columns)
    // from propagating up and widening the whole modal on a narrow viewport:
    // the table scrolls sideways inside this box instead.
    <div className="max-h-[320px] overflow-auto [contain:inline-size]">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
          <tr>
            <th className="w-6 px-2 py-2" />
            <th className="px-3 py-2">Booking ID</th>
            <th className="px-3 py-2">Penerima</th>
            <th className="px-3 py-2">Tujuan</th>
            <th className="px-3 py-2">Layanan</th>
            <th className="px-3 py-2 text-right">Item</th>
            <th className="px-3 py-2 text-right">Koli</th>
            <th className="px-3 py-2 text-right">Total berat</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.toCreate.map((row) => (
            <ShipmentGroupRow
              key={row.bookingId}
              row={row}
              route={route}
              expanded={!!expanded[row.bookingId]}
              onToggle={() => toggle(row.bookingId)}
            />
          ))}
        </tbody>
      </table>
      {preview.summary.toCreateShipments > preview.toCreate.length && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[11px] text-text-soft-400">
          Menampilkan {preview.toCreate.length} dari{' '}
          {preview.summary.toCreateShipments}.
        </div>
      )}
    </div>
  );
};

/**
 * Destination cell of a preview row. On a Titik Dropoff upload a row whose
 * dropoff was left blank lands AT the anchor — show that as the anchor's chip
 * (full address on hover) so it reads apart from a row keeping its own address.
 */
const DestinationCell: React.FC<{
  address: string;
  route: PreviewRoute | null;
}> = ({ address, route }) => {
  const toAnchor =
    route?.anchorRole === 'DESTINATION' &&
    route.anchorAddress != null &&
    address === route.anchorAddress;
  return (
    // Wraps (max two lines) instead of a single-line truncate: a nowrap cell
    // makes a long address the table's min-content width, which pushes the
    // whole modal wider than a narrow viewport.
    <td className="px-3 py-1.5">
      {toAnchor ? (
        <span
          title={address}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-primary/40 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
        >
          <i className="ri-map-pin-2-line" aria-hidden="true" />
          {route?.anchorName ?? 'Hub / pitstop'}
        </span>
      ) : (
        <span
          className="line-clamp-2 min-w-[8rem] text-text-sub-600"
          title={address}
        >
          {address || '—'}
        </span>
      )}
    </td>
  );
};

const ShipmentGroupRow: React.FC<{
  row: ImportPreviewShipmentRow;
  route: PreviewRoute | null;
  expanded: boolean;
  onToggle: () => void;
}> = ({ row, route, expanded, onToggle }) => (
  <>
    <tr
      className="cursor-pointer border-t border-stroke-soft-200 hover:bg-bg-weak-50"
      onClick={onToggle}
    >
      <td className="px-2 py-1.5 text-text-soft-400">
        <i
          className={`text-sm ${expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}
        />
      </td>
      <td className="px-3 py-1.5 font-mono">{row.bookingId}</td>
      <td className="px-3 py-1.5">{row.receiverName}</td>
      <DestinationCell address={row.destinationAddress} route={route} />
      <td className="px-3 py-1.5">{row.serviceType}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">{row.itemCount}</td>
      {/* Sum over ALL AWB items (BE caps `items` at PREVIEW_LIST_CAP) — never
          reduce over `row.items` here. */}
      <td className="px-3 py-1.5 text-right tabular-nums">
        {row.totalKoli ?? '—'}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">{row.totalWeight}</td>
    </tr>
    {expanded && (
      <tr className="bg-bg-weak-50/60">
        <td />
        <td colSpan={7} className="px-3 py-2">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
            Item
          </div>
          <table className="w-full text-left text-[11px]">
            <thead className="text-text-soft-400">
              <tr>
                <th className="py-1 pr-3">Baris</th>
                <th className="py-1 pr-3">Nama item</th>
                <th className="py-1 pr-3 text-right">Koli</th>
                <th className="py-1 pr-3 text-right">Berat</th>
              </tr>
            </thead>
            <tbody className="text-text-sub-600">
              {row.items.map((it) => (
                <tr
                  key={`${row.bookingId}-${it.rowNumber}-${it.itemName}`}
                  className="border-t border-stroke-soft-200"
                >
                  <td className="py-1 pr-3 tabular-nums text-text-soft-400">
                    {it.rowNumber}
                  </td>
                  <td className="py-1 pr-3 font-mono">{it.itemName}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {it.koli ?? 1}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {it.weight}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </>
);

const DuplicatesTable: React.FC<{ preview: ImportPreviewResult }> = ({
  preview,
}) => {
  if (preview.summary.duplicates === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-soft-400">
        Tidak ada duplikat.
      </div>
    );
  }
  return (
    <div className="max-h-[280px] overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
          <tr>
            <th className="px-3 py-2">Booking ID</th>
            <th className="px-3 py-2">Waybill existing</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Ditambah</th>
            <th className="px-3 py-2 text-right">Dilewati</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.duplicates.map((d, idx) => (
            <tr
              key={`${d.bookingId}-${idx}`}
              className="border-t border-stroke-soft-200 align-top"
            >
              <td className="px-3 py-2 font-mono">{d.bookingId}</td>
              <td className="px-3 py-2 font-mono text-[11px] text-text-sub-600">
                {d.existingWaybill}
              </td>
              <td className="px-3 py-2">
                {/* Labelled, not the raw enum — v2 added AT_HUB / RETURNED /
                    FAILED, which read poorly as SCREAMING_SNAKE. */}
                <span className="font-medium text-text-sub-600">
                  {getShipmentStatusLabel(d.existingStatus)}
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-success-dark">
                {d.appendItemCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-text-soft-400">
                {d.skippedItemCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ErrorsTable: React.FC<{
  preview: ImportPreviewResult;
  onDownload: () => void;
}> = ({ preview, onDownload }) => {
  if (preview.summary.errors === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-soft-400">
        Tidak ada error. Siap untuk diimpor.
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <p className="text-[11px] text-text-sub-600">
          {preview.summary.errors} error memblokir commit.
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <i className="ri-download-2-line text-sm" />
          Download error CSV
        </button>
      </div>
      <div className="max-h-[260px] overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
            <tr>
              <th className="px-3 py-2">Baris</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Nilai</th>
              <th className="px-3 py-2">Alasan</th>
            </tr>
          </thead>
          <tbody className="text-text-strong-950">
            {preview.errors.map((e, idx) => (
              <tr
                key={`${e.rowNumber}-${e.field}-${idx}`}
                className="border-t border-stroke-soft-200"
              >
                <td className="px-3 py-1.5 tabular-nums text-text-soft-400">
                  {e.rowNumber}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{e.field}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-text-sub-600">
                  {e.value || (
                    <span className="italic text-text-soft-400">kosong</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-error-base">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportModal;
